import { Divider } from "@/Divider";
import { LayoutHeader } from "@/Header";
import { createFileRoute } from "@tanstack/react-router";
import pet1Src from "$/pet-1.svg";
import NotificationCard from "@/NotificationCard";
import DiaryHeader from "@/DiaryHeader";
import CommentItem from "@/CommentItem";
import { CalendarNew as CalendarS } from "@/CalendarNew";
import {
  useUserNoticeDetail,
  useUserNoticeReplySave,
  useUserNoticeReply,
} from "~/services/customer/notice";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Dialog } from "@/Dialog";
import { ImageViewer } from "@/ImageViewer";
import { getAttachmentMediaType } from "%/mediaType";
import DocAttachment from "@/common/DocAttachment";

// URL 확장자로 미디어 타입 판단 (image | video | document)
const getMediaType = getAttachmentMediaType;

interface SearchDetailParams {
  seq?: string;
  send?: string;
  petSeq?: string;
}

export const Route = createFileRoute("/customer/layout/inform-detail")({
  component: InformDetail,
  validateSearch: (search: Record<string, unknown>): SearchDetailParams => {
    return {
      seq: search.seq as string,
      send: search.send as string,
      petSeq: search.petSeq as string,
    };
  },
});

function InformDetail() {
  const { seq, send, petSeq } = Route.useSearch();
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [replyMessage, setReplyMessage] = useState(""); // 댓글 입력 상태

  // Dialog 상태
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");

  // ImageViewer 상태
  const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  // 플랫폼 감지
  const userAgent = navigator.userAgent;
  const isIOS = useMemo(() => /MT_I/i.test(userAgent), [userAgent]);
  const isAndroid = useMemo(() => /MT_A/i.test(userAgent), [userAgent]);

  // API 호출
  const {
    data: noticeDetail,
    isLoading,
    error,
  } = useUserNoticeDetail({
    notiRecvSeq: parseInt(seq || "0"),
  });

  // 댓글 저장 API
  const { mutate: saveReply, isPending: isReplySaving } =
    useUserNoticeReplySave();

  // 댓글 목록 조회 API
  const { data: repliesData, refetch: refetchReplies } = useUserNoticeReply(
    parseInt(seq || "0")
  );

  console.log("noticeDetail", noticeDetail);

  // 이미지 데이터 (API에서 받은 photos 사용)
  const [images, setImages] = useState<
    {
      fileSeq: number;
      fileUrl: string;
      thumbnailUrl?: string | null;
      thumbnailStatus?: string | null;
      muteYn?: string | null;
      expiredYn?: string | null;
    }[]
  >(
    []
  );

  // API 데이터가 로드되면 이미지 설정
  useEffect(() => {
    if (noticeDetail?.data?.photos && noticeDetail.data.photos.length > 0) {
      setImages(
        noticeDetail.data.photos.map((photo) => ({
          fileSeq: photo.fileSeq,
          fileUrl: photo.fileUrl,
          thumbnailUrl: photo.thumbnailUrl ?? null,
          thumbnailStatus: photo.thumbnailStatus ?? null,
          muteYn: photo.muteYn ?? null,
          expiredYn: (photo as { expiredYn?: string | null }).expiredYn ?? null,
        }))
      );
    } else {
      setImages([]);
    }
  }, [noticeDetail?.data?.photos]);

  // 발송 후 사업장이 수정한 경우의 수정 일시. 발송 시점에는 등록/수정 시각이 같다.
  // TODO 보호자 화면에는 '수정됨' 노출 보류 — 재개 시 아래 블록과 DiaryHeader 의 editedAt prop 주석 해제
  // const editedAt = useMemo(() => {
  //   const regist = noticeDetail?.data?.dtRegist;
  //   const update = noticeDetail?.data?.dtUpdate;
  //   if (!regist || !update) return null;
  //   const registTime = new Date(regist).getTime();
  //   const updateTime = new Date(update).getTime();
  //   if (isNaN(registTime) || isNaN(updateTime)) return null;
  //   if (updateTime - registTime <= 1000) return null;
  //   const d = new Date(update);
  //   const yyyy = d.getFullYear();
  //   const mm = String(d.getMonth() + 1).padStart(2, "0");
  //   const dd = String(d.getDate()).padStart(2, "0");
  //   const hh = String(d.getHours()).padStart(2, "0");
  //   const mi = String(d.getMinutes()).padStart(2, "0");
  //   return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
  // }, [noticeDetail?.data?.dtRegist, noticeDetail?.data?.dtUpdate]);

  // 공지사항·일정공지 알림장은 댓글 작성/노출 불가 (백엔드에서도 저장 차단)
  const isReplyBlocked =
    noticeDetail?.data?.cdNotiType === "NOTI_ANNOUNCEMENT" ||
    noticeDetail?.data?.cdNotiType === "NOTI_SCHEDULE";

  // 미디어 URL 배열 메모이제이션
  const mediaUrls = useMemo(() => {
    return images.map((img) => img.fileUrl);
  }, [images]);

  // 현재 선택된 미디어 타입
  // ImageViewer 는 image/video 만 지원한다. 문서 첨부는 뷰어로 열지 않으므로 image 로 폴백.
  const currentMediaType = useMemo((): "image" | "video" => {
    if (mediaUrls[selectedMediaIndex]) {
      return getMediaType(mediaUrls[selectedMediaIndex]) === "video"
        ? "video"
        : "image";
    }
    return "image";
  }, [mediaUrls, selectedMediaIndex]);

  // 미디어 클릭 핸들러
  const handleMediaClick = useCallback((index: number) => {
    setSelectedMediaIndex(index);
    setIsMediaViewerVisible(true);
  }, []);

  // ImageViewer 닫기
  const closeMediaViewer = useCallback(() => {
    setIsMediaViewerVisible(false);
  }, []);

  // 개별 미디어 다운로드
  const handleMediaDownload = useCallback(async () => {
    const currentUrl = mediaUrls[selectedMediaIndex];
    if (!currentUrl) return;

    const isVideo = getMediaType(currentUrl) === "video";

    try {
      if (isAndroid) {
        if (isVideo && (window.mung as any)?.downloadVideo) {
          (window.mung as any).downloadVideo(currentUrl);
        } else {
          window.mung?.downloadImage(currentUrl);
        }
      } else if (isIOS) {
        if (isVideo) {
          window.webkit?.messageHandlers?.mung?.postMessage(["downloadVideo", currentUrl]);
        } else {
          window.webkit?.messageHandlers?.mung?.postMessage(["downloadImage", currentUrl]);
        }
      } else {
        // 웹 다운로드
        const response = await fetch(currentUrl);
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        const ext = isVideo ? ".mp4" : ".jpg";
        a.download = `media-${selectedMediaIndex + 1}-${Date.now()}${ext}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      console.error("다운로드 실패:", error);
      setDialogTitle("다운로드 실패");
      setDialogMessage("파일 다운로드에 실패했습니다.");
      setIsDialogVisible(true);
    }
  }, [mediaUrls, selectedMediaIndex, isAndroid, isIOS]);

  // 전체 다운로드 핸들러
  const handleDownloadAll = useCallback(async () => {
    if (images.length === 0) return;

    try {
      for (let i = 0; i < images.length; i++) {
        const url = images[i].fileUrl;
        const isVideo = getMediaType(url) === "video";

        if (isAndroid) {
          if (isVideo && (window.mung as any)?.downloadVideo) {
            (window.mung as any).downloadVideo(url);
          } else {
            window.mung?.downloadImage(url);
          }
        } else if (isIOS) {
          if (isVideo) {
            window.webkit?.messageHandlers?.mung?.postMessage(["downloadVideo", url]);
          } else {
            window.webkit?.messageHandlers?.mung?.postMessage(["downloadImage", url]);
          }
        } else {
          // 웹 다운로드
          const response = await fetch(url);
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          const ext = isVideo ? ".mp4" : ".jpg";
          a.download = `media-${i + 1}-${Date.now()}${ext}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);
          // 다운로드 간 딜레이
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      console.error("전체 다운로드 실패:", error);
      setDialogTitle("다운로드 실패");
      setDialogMessage("파일 다운로드에 실패했습니다.");
      setIsDialogVisible(true);
    }
  }, [images, isAndroid, isIOS]);

  // 댓글 등록 함수
  const handleReplySubmit = () => {
    if (!replyMessage.trim()) {
      setDialogTitle("입력 오류");
      setDialogMessage("댓글을 입력해주세요.");
      setIsDialogVisible(true);
      return;
    }

    if (!noticeDetail?.data?.noticeSeq) {
      setDialogTitle("오류");
      setDialogMessage("알림 정보를 찾을 수 없습니다.");
      setIsDialogVisible(true);
      return;
    }

    saveReply(
      {
        noticeSeq: noticeDetail.data.noticeSeq,
        message: replyMessage.trim(),
        petSeq: parseInt(petSeq || "0"),
      },
      {
        onSuccess: (response) => {
          console.log("댓글 저장 성공:", response);
          // setReplyMessage(""); // 입력창 초기화
          // setDialogTitle("성공");
          // setDialogMessage("댓글이 성공적으로 등록되었습니다.");
          // setIsDialogVisible(true);
          // 댓글 목록 새로고침
          refetchReplies();
        },
        onError: (error) => {
          console.error("댓글 저장 실패:", error);
          setDialogTitle("오류");
          setDialogMessage("댓글 등록에 실패했습니다. 다시 시도해주세요.");
          setIsDialogVisible(true);
        },
      }
    );
  };
  useEffect(() => {
    if (
      noticeDetail?.data?.schedules &&
      noticeDetail.data.schedules.length > 0
    ) {
      setSelectedDates(noticeDetail.data.schedules);
    } else {
      setSelectedDates([]);
    }
  }, [noticeDetail?.data?.schedules]);
  function PetImageGallery({
    images,
    onMediaClick,
  }: {
    images: {
      fileSeq: number;
      fileUrl: string;
      thumbnailUrl?: string | null;
      thumbnailStatus?: string | null;
      expiredYn?: string | null;
    }[];
    onMediaClick: (index: number) => void;
  }) {
    // 이미지가 없으면 렌더링하지 않음
    if (!images || images.length === 0) {
      return null;
    }

    // 보존기간 만료로 삭제된 사진 — 액박/소실 대신 안내 + 기본 이미지
    const ExpiredMedia = ({ small }: { small?: boolean }) => (
      <div
        className={`flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 bg-gray-100 w-full ${small ? "h-29" : "h-40"}`}
      >
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 6.75h.008v.008H18V6.75zM2.25 6a3.75 3.75 0 013.75-3.75h12A3.75 3.75 0 0121.75 6v12A3.75 3.75 0 0118 21.75H6A3.75 3.75 0 012.25 18V6z"
          />
        </svg>
        <span className="text-[11px] text-gray-500">보관 기간이 만료되어</span>
        <span className="text-[11px] text-gray-500">삭제된 사진입니다</span>
      </div>
    );

    if (images.length <= 3) {
      // 세로 1열, 각 이미지마다 margin
      return (
        <div className="flex flex-col gap-3">
          {images.map((src, idx) => {
            if (src.expiredYn === "Y") {
              return (
                <div key={idx} className="relative">
                  <ExpiredMedia />
                </div>
              );
            }
            const mediaType = getMediaType(src.fileUrl || "");
            const isThumbnailReady =
              src.thumbnailStatus === "COMPLETE" && !!src.thumbnailUrl;
            const displayUrl = isThumbnailReady
              ? src.thumbnailUrl!
              : src.fileUrl;
            return (
              <div
                key={idx}
                className="relative cursor-pointer"
                onClick={
                  mediaType === "document"
                    ? undefined
                    : () => onMediaClick(idx)
                }
              >
                {mediaType === "video" ? (
                  <>
                    {isThumbnailReady ? (
                      <img
                        src={displayUrl}
                        alt={`Video thumbnail ${idx + 1}`}
                        className="object-cover w-full rounded-xl"
                      />
                    ) : (
                      <div className="flex justify-center items-center w-full h-40 bg-gray-300 rounded-xl">
                        <svg
                          className="w-10 h-10 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                        </svg>
                      </div>
                    )}
                    {/* 동영상 재생 아이콘 오버레이 */}
                    <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-xl pointer-events-none">
                      <svg
                        className="w-12 h-12 text-white opacity-80"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </>
                ) : mediaType === "document" ? (
                  <div className="h-40 w-full">
                    <DocAttachment url={src.fileUrl} />
                  </div>
                ) : (
                  <img
                    src={displayUrl || ""}
                    alt={`pet-${idx}`}
                    className="object-cover w-full rounded-xl"
                    loading="lazy"
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    }
    // 4개 이상: 2열 그리드로 모두 표시
    return (
      <div className="grid grid-cols-2 gap-3">
        {images.map((src, idx) => {
          if (src.expiredYn === "Y") {
            return (
              <div key={idx} className="relative">
                <ExpiredMedia small />
              </div>
            );
          }
          const mediaType = getMediaType(src.fileUrl || "");
          const isThumbnailReady =
            src.thumbnailStatus === "COMPLETE" && !!src.thumbnailUrl;
          const displayUrl = isThumbnailReady
            ? src.thumbnailUrl!
            : src.fileUrl;
          return (
            <div
              key={idx}
              className="relative cursor-pointer"
              onClick={
                mediaType === "document" ? undefined : () => onMediaClick(idx)
              }
            >
              {mediaType === "video" ? (
                <>
                  {isThumbnailReady ? (
                    <img
                      src={displayUrl}
                      alt={`Video thumbnail ${idx + 1}`}
                      className="object-cover w-full rounded-xl h-29"
                    />
                  ) : (
                    <div className="flex justify-center items-center w-full h-29 bg-gray-300 rounded-xl">
                      <svg
                        className="w-8 h-8 text-gray-500"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
                      </svg>
                    </div>
                  )}
                  {/* 동영상 재생 아이콘 오버레이 */}
                  <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-xl pointer-events-none">
                    <svg
                      className="w-10 h-10 text-white opacity-80"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </>
              ) : mediaType === "document" ? (
                <div className="h-29 w-full">
                  <DocAttachment url={src.fileUrl} />
                </div>
              ) : (
                <img
                  src={displayUrl || ""}
                  alt={`pet-${idx}`}
                  className="object-cover w-full rounded-xl h-29"
                  loading="lazy"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }
  const handleDialogClose = () => {
    setIsDialogVisible(false);
    setDialogMessage("");
    setDialogTitle("");
  };

  return (
    <>
      {/* 헤더 배경을 불투명 흰색으로 고정.
          앱뷰에서 ImageViewer(fixed)가 main 스크롤 컨테이너에 갇혀 헤더 아래 깔리는데,
          헤더가 backdrop-blur 만 있고 배경이 없으면 뷰어의 검정이 비쳐 헤더가 어둡게 보인다.
          비즈 알림장 상세와 동일하게 헤더는 흰색으로 유지되도록 한다. */}
      <LayoutHeader
        title={send === "N" ? "받은 알림장" : "보낸 알림장"}
        hasBackBtn
        hasFootPrint={false}
        className="bg-white"
      />
      <div className="p-2 flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5]">
        {/* 상단 프로필/타이틀 */}
        <div className="p-2">
          {isLoading ? (
            <div className="py-4 text-center">로딩 중...</div>
          ) : error ? (
            <div className="py-4 text-center text-red-500">
              에러가 발생했습니다.
            </div>
          ) : (
            <DiaryHeader
              profileImg={noticeDetail?.data?.profileImgUrl || pet1Src}
              petName={noticeDetail?.data?.petName || "또치"}
              date={
                noticeDetail?.data?.dtSend
                  ? new Date(noticeDetail.data.dtSend).toLocaleDateString()
                  : "2024.03.05"
              }
              isExistPhoto={images.length > 0}
              onDownload={handleDownloadAll}
              // editedAt={editedAt}
            />
          )}
          {noticeDetail?.data.cdNotiType == "NOTI_SCHEDULE" && (
            <CalendarS
              // label={"또치 스케줄"}
              disabled={false}
              schedules={{}}
              multiSelect={true}
              selectedDays={selectedDates}
            />
          )}
          {/* 알림장 본문 */}
          {!isLoading && !error && (
            <NotificationCard>
              <PetImageGallery images={images} onMediaClick={handleMediaClick} />
              <p
                className={`text-sm text-gray-500 whitespace-pre-line ${
                  images.length > 0 ? "pt-5" : ""
                }`}
              >
                {noticeDetail?.data?.notiContents || "알림 내용이 없습니다."}
              </p>
              <div>
                {(noticeDetail?.data?.cdNotiType?.includes(
                  "NOTI_RESERV_CONFIRM"
                ) ||
                  noticeDetail?.data?.cdNotiType?.includes(
                    "NOTI_RESERV_CANCEL"
                  )) && (
                  <button className="px-4 py-2 mt-2 rounded border border-yellow">
                    <p className="text-sm text-yellow">예약 상세 보기</p>
                  </button>
                )}
              </div>
            </NotificationCard>
          )}
          {noticeDetail?.data?.activities &&
            noticeDetail.data.activities.length > 0 && (
              <NotificationCard>
                <div className="grid grid-cols-3 gap-x-2 gap-y-3">
                  {noticeDetail.data.activities.map((item, index: number) => (
                    <div key={index} className="flex flex-col items-center">
                      <div className="text-sm font-semibold text-center">
                        {item.activityType}
                      </div>
                      <div className="mt-2 border rounded-lg px-6 py-2 text-[#FF9934] text-sm font-medium bg-white w-full text-center">
                        {item.activityValue}
                      </div>
                    </div>
                  ))}
                </div>
              </NotificationCard>
            )}
        </div>
        {/* 댓글 — 공지사항·일정공지 알림장에는 댓글 기능을 제공하지 않음 */}
        {!isReplyBlocked && (
        <div className="p-2">
          <div className="mb-2 text-sm font-semibold">댓글</div>
          <Divider line />
          <div className="pt-5 pb-20 space-y-4">
            {repliesData?.data && repliesData.data.length > 0 ? (
              repliesData.data.map((reply, index) => (
                <CommentItem
                  key={index}
                  profileImg={
                    reply.profileImgUrl || "/images/default-profile.png"
                  }
                  name={reply.writer || "알 수 없음"}
                  content={reply.message || ""}
                  date={
                    reply.dtWrite
                      ? new Date(reply.dtWrite).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : ""
                  }
                  // onReply={() => {
                  //   /* 답글쓰기 로직 */
                  // }}
                />
              ))
            ) : (
              <div className="py-4 text-sm text-center text-gray-500">
                아직 댓글이 없습니다.
              </div>
            )}
          </div>
          {/* 댓글 입력창 */}
          <div className="sticky bottom-14 w-full bg-[#F5F5F5] px-2 py-2 flex items-center">
            <input
              type="text"
              placeholder="댓글을 입력하세요"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleReplySubmit();
                }
              }}
              className="flex-1 min-w-0 px-3 py-2 text-sm bg-white rounded border border-gray-300"
            />
            <button
              onClick={handleReplySubmit}
              disabled={isReplySaving || !replyMessage.trim()}
              className="flex-shrink-0 whitespace-nowrap px-4 py-2 ml-2 text-sm bg-white rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReplySaving ? "등록 중..." : "등록"}
            </button>
          </div>
        </div>
        )}
      </div>
      {/* Dialog */}
      <Dialog visible={isDialogVisible} onClose={handleDialogClose}>
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">{dialogTitle}</h3>
          <p className="mb-4 text-gray-600">{dialogMessage}</p>
          <button
            onClick={handleDialogClose}
            className="px-4 py-2 text-white bg-orange-500 rounded-lg hover:bg-orange-600"
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* ImageViewer - 미디어 전체화면 뷰어 */}
      {isMediaViewerVisible && mediaUrls.length > 0 && (
        <ImageViewer
          imageUrl={mediaUrls[selectedMediaIndex]}
          isVisible={isMediaViewerVisible}
          onClose={closeMediaViewer}
          onDownload={handleMediaDownload}
          images={mediaUrls}
          currentIndex={selectedMediaIndex}
          mediaType={currentMediaType}
          defaultMutedList={images.map((img) => img.muteYn === "Y")}
          onImageChange={(index) => {
            setSelectedMediaIndex(index);
          }}
        />
      )}
    </>
  );
}
